import * as React from 'react';

import styles from './regionManager.scss';

const regionAttributeName = 'data-navigationregion';

let isFocusedState = false;

export const enum Region {
    Search = 0,
    LeftNav = 1,
    ModuleSwitcher = 2,
    CommandBar = 3,
    HubList = 4,
    DetailsView = 5,
}

/**
 * Aria landmarks definition: https://www.w3.org/TR/WCAG20-TECHS/ARIA11.html
 */
export const enum AriaLandmark {
    Banner = 'banner',
    Navigation = 'navigation',
    Main = 'main',
    Search = 'search',
    ContentInfo = 'contentinfo',
}

const regionOrder: Region[] = Array(5)
    .fill(1)
    .map((e, i) => i);

export const domFunctions = {
    getElementForRegion: (region: Region) =>
        document.querySelector(regionSelector(region)) as HTMLElement,
    getActiveElement: () => document.activeElement as HTMLElement,
    elementContainsElement: (parent: HTMLElement, child: HTMLElement) => parent.contains(child),
    focusRegion: (element: HTMLElement) => {
        element.focus();
        element.classList.remove(styles.inactiveRegion);
        element.classList.add(styles.focusedRegion);
    },
    blurRegion: (element: HTMLElement) => {
        element.classList.add(styles.inactiveRegion);
        element.classList.remove(styles.focusedRegion);
    },
};

export type DomInterface = typeof domFunctions;

const regionSelector = (region: Region) => `*[${regionAttributeName}="${region}"]`;

const numberIsNotNull = (val: number) => val || val === 0;

const getRegionForElement = (element: HTMLElement): Region => {
    if (element && element.parentElement !== undefined) {
        if (element.hasAttribute(regionAttributeName)) {
            return element.attributes[regionAttributeName].nodeValue;
        }

        return getRegionForElement(element.parentElement);
    }

    return null;
};

export const getNextRegion = (domInterface: DomInterface, reverse?: boolean): Region => {
    const traversalOrder = reverse ? regionOrder.slice().reverse() : regionOrder;

    // If there is no current region, set it to the end of the focus order so we can start from the beginning
    const defaultStartingPoint = traversalOrder.slice(-1)[0];
    const currentRegion = getRegionForElement(domInterface.getActiveElement());
    const currentRegionOrDefault = numberIsNotNull(currentRegion)
        ? currentRegion
        : defaultStartingPoint;

    const procedingRegions = traversalOrder.filter(region => region > currentRegionOrDefault);
    const precedingRegions = traversalOrder.filter(region => region < currentRegionOrDefault);
    const searchOrder = reverse
        ? precedingRegions.concat(procedingRegions)
        : procedingRegions.concat(precedingRegions);

    // Find the next region that exists in the DOM currently
    const nextRegion = searchOrder.find(region => !!domInterface.getElementForRegion(region));
    return nextRegion;
};

const getNextElement = (domInterface: DomInterface, reverse?: boolean) => {
    const nextRegion = getNextRegion(domInterface, reverse);
    return numberIsNotNull(nextRegion) && domInterface.getElementForRegion(nextRegion);
};

const focusNextElement = (domInterface: DomInterface, reverse?: boolean) => {
    const nextRegion = getNextRegion(domInterface, reverse);
    const nextElement = getNextElement(domInterface, reverse);
    if (nextElement) {
        regionOrder
            .filter(region => region !== nextRegion)
            .map(region => domInterface.getElementForRegion(region))
            .forEach(element => element && domInterface.blurRegion(element));

        isFocusedState = true;
        domInterface.focusRegion(nextElement);
    }
};

/**
 * This component is placed directly around an HTML element (<div>, <ul>, ...), it decorates it with logic necessary for region navigation
 * Placing it on component classes that don't directly render an HTML element (and instead nest more component classes) is not supported.
 */
export function RegionBoundary(props: {
    region: Region;
    ariaLandmark: AriaLandmark;
    ariaLabel: string;
    children?: React.ReactNode;
}) {
    const child = React.Children.only(props.children) as React.ReactHTMLElement<any>;
    const newChild = React.cloneElement(child, {
        className: `${child.props.className || ''} ${styles.focusRegion}`,
        'data-navigationregion': props.region,
        tabIndex: -1,
        role: props.ariaLandmark.toString(),
        'aria-label': props.ariaLabel,
        onBlur: e => (e.target as HTMLElement).classList.remove(styles.focusedRegion),
    });
    return newChild;
}

const removeAllAppliedStyles = () => {
    isFocusedState = false;
    regionOrder
        .map(region => domFunctions.getElementForRegion(region))
        .forEach(element => {
            if (element) {
                element.classList.remove(styles.inactiveRegion);
                element.classList.remove(styles.focusedRegion);
            }
        });
};

const removeBlurs = () => {
    isFocusedState = false;
    regionOrder
        .map(region => domFunctions.getElementForRegion(region))
        .forEach(element => element && element.classList.remove(styles.inactiveRegion));
};

window.addEventListener('keydown', {
    handleEvent: (event: KeyboardEvent) => {
        if (event.ctrlKey && event.key === 'F6') {
            focusNextElement(domFunctions, event.shiftKey);
        } else if (isFocusedState) {
            removeBlurs();
        }
    },
});

window.addEventListener('mousemove', {
    handleEvent: () => isFocusedState && removeAllAppliedStyles(),
});
